import { ensureError, formatBytes } from '@sortera/ee-utils';
import { compressImage } from './compression.js';
import { ERROR_NAME_MAX_UPLOAD_SIZE_EXCEEDED, ERROR_NAME_UPLOAD_FAILED, MAX_IMAGE_UPLOAD_SIZE, UPLOAD_RETRY_LIMIT, } from './constants.js';
import { withRetry } from './utils.js';
/** Uploads files to the server along with metadata. */
export async function uploadImages(files, meta, options) {
    if (!files)
        throw new Error('files cannot be null');
    if (!meta?.type || !meta.company || !meta.orderId || !meta.user) {
        throw new Error('incomplete metadata' + JSON.stringify(meta));
    }
    if (files.length === 0)
        throw new Error('no files to upload');
    const filesArr = [...files];
    const formData = new FormData();
    const uploadDate = Date.now();
    formData.append('metaData', JSON.stringify(meta));
    const replaceWithCompressedPromises = filesArr.map(async (file, i) => {
        if (options.disableCompression)
            return;
        try {
            const compressedFile = await compressImage(file);
            filesArr[i] = compressedFile;
        }
        catch {
            // If we can't compress, do nothing and just upload the original file
        }
    });
    await Promise.all(replaceWithCompressedPromises);
    const totalSize = getTotalFileSize(filesArr);
    if (totalSize > MAX_IMAGE_UPLOAD_SIZE) {
        const msg = `Total file size exceeds the maximum allowed size. ${formatBytes(totalSize)} / ${formatBytes(MAX_IMAGE_UPLOAD_SIZE)}`;
        const err = new Error(msg);
        err.name = ERROR_NAME_MAX_UPLOAD_SIZE_EXCEEDED;
        throw err;
    }
    filesArr.forEach((file, index) => {
        formData.append(`file-${index}`, file, ('name' in file && file.name) || uploadDate.toString());
    });
    options?.hooks?.onBeforeImageUploadSent?.(filesArr);
    return withRetry(UPLOAD_RETRY_LIMIT, remainingRetries => doUploadImages(filesArr, formData, remainingRetries, options));
}
async function doUploadImages(files, formData, remainingRetries, options) {
    try {
        const response = await options.http.post('/api/OrderImage/images', formData);
        const { data } = response;
        // TODO: I think this is dead code, the request should throw in case of a failed request
        const dataWithErrors = data.filter(r => !r.success);
        if (dataWithErrors.length > 0) {
            const msg = `Image upload failed for ${dataWithErrors.length}/${data.length} images. Errors: ${dataWithErrors.map(e => `"${e.errorMessage}"`).join('; ')}`;
            const err = new Error(msg);
            err.name = ERROR_NAME_UPLOAD_FAILED;
            options?.hooks?.onError?.(err, [...files], remainingRetries);
            throw err;
        }
        return data.map(r => ({ src: `${options.cdnUrl}/${r.imageName}` }));
    }
    catch (err) {
        const error = ensureError(err);
        error.name = ERROR_NAME_UPLOAD_FAILED;
        options?.hooks?.onError?.(error, [...files], remainingRetries);
        throw error;
    }
}
function getTotalFileSize(files) {
    return [...files].reduce((total, file) => total + file.size, 0);
}
